import { Button } from "@svea/design-system-components-react";
import { Stack, Text, useText } from "@sveabank/cc-common";
import { isAfter, isBefore, isWithinInterval } from "date-fns";
import { v4 as getUuid } from "uuid";
import Paper from "../../components/paper";
import { usePageContext } from "../../hooks/context";
import { useUser } from "../../hooks/useUser";
import {
	useCreditScoreMutation,
	useDeleteApplicationMutation,
	useInitApplicationMutation,
} from "../../services/application";
import { useLazyFetchPackageSignersQuery } from "../../services/package-agreement";
import type { InactiveEngagement } from "../../types/application";
import { CreditCheckFailedError, InvalidApplicantError, useDashboard } from "./hooks";

type Props = {
	engagement: InactiveEngagement;
	setIsNewApplicationModalOpen: (arg: boolean) => void;
	setNonSignatorModalOpen: (arg: boolean) => void;
	setNewApplicationId: (arg: string) => void;
	withPromos: string[];
};

const NotCustomer = ({
	engagement,
	setIsNewApplicationModalOpen,
	setNewApplicationId,
	setNonSignatorModalOpen,
	withPromos,
}: Props) => {
	const [t, texts] = useText();
	const { getLoggedInUser } = useUser();
	const user = getLoggedInUser();

	const { setNonVerifiedApplication, promotions } = usePageContext(useDashboard);

	const [initApplicationMutation, { isLoading: initApplicationLoading }] =
		useInitApplicationMutation();

	const [deleteApplicationMutation] = useDeleteApplicationMutation();

	const [fetchPackageSigners, { isLoading: isLoadingPackageSigners }] =
		useLazyFetchPackageSignersQuery();

	const [creditScoreMutation, { isLoading: isLoadingCreditScore }] = useCreditScoreMutation();

	const isInitingApplication =
		initApplicationLoading || isLoadingPackageSigners || isLoadingCreditScore;

	const initApplication = async (orgNr: string) => {
		const promotionCodes: string[] = [];

		if (withPromos) {
			promotionCodes.push(...withPromos);
		}

		if (promotions && promotions.length > 0) {
			const today = new Date();
			const availablePromotionsNotIncluded = promotions
				.filter((p) =>
					isWithinInterval(today, {
						start: new Date(p.promotionStarts),
						end: new Date(p.promotionEnds),
					}),
				)
				.map((p) => p.promotion.promotionCode)
				.filter((pc) => !promotionCodes.includes(pc));
			promotionCodes.push(...availablePromotionsNotIncluded);
		}

		const applicationId = getUuid();
		setNonVerifiedApplication(applicationId);
		await initApplicationMutation({ orgNr, applicationId, promotionCodes }).unwrap();

		const res = await fetchPackageSigners(applicationId).unwrap();

		if (res.find((signer) => signer.nationalId === user.bankIdUser.bankIdNumber)) {
			try {
				await creditScoreMutation({ orgNr, applicationId }).unwrap();
				return applicationId;
			} catch {
				await deleteApplicationMutation(applicationId);
				throw new CreditCheckFailedError();
			}
		}

		await deleteApplicationMutation(applicationId);
		throw new InvalidApplicantError();
	};

	const handleNewApplicationClick = async (orgNr: string) => {
		try {
			const applicationId = await initApplication(orgNr);
			setNewApplicationId(applicationId);
			setIsNewApplicationModalOpen(true);
		} catch (e) {
			if (e instanceof InvalidApplicantError) {
				setNonSignatorModalOpen(true);
			} else if (e instanceof CreditCheckFailedError) {
				return;
			} else {
				throw e;
			}
		}
	};

	return (
		<Paper padding="base" key={engagement.orgNr}>
			<Stack gap={2} align="start">
				<Stack gap={2}>
					<Text component="h4" className="text-text-primary text-xl" bold>
						{engagement.name}
					</Text>
					<Text>{engagement.orgNrDisplayName}</Text>
				</Stack>
				<Button
					onPress={() => {
						void handleNewApplicationClick(engagement.orgNr);
					}}
					isPending={isInitingApplication}
					iconEnd="chevronRight"
					data-tracking-action="Onboarding: Start Application"
					data-tracking-name="Dashboard"
				>
					{t(texts.dashboardPage.existingCompanyButtonText)}
				</Button>
			</Stack>
		</Paper>
	);
};

export default NotCustomer;
